import React, { useEffect } from 'react';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { useAppData } from '../contexts/AppDataContext';
import { Result, Button, Spin } from 'antd';
import { LockOutlined } from '@ant-design/icons';

const SUPER_ADMIN_ROUTES = ['/users'];
const SUPER_ADMIN_ROUTE_PREFIX = '/admin';

const isSuperAdminRoute = (pathname) => (
  SUPER_ADMIN_ROUTES.includes(pathname) || pathname.startsWith(SUPER_ADMIN_ROUTE_PREFIX)
);

const RedirectBack = ({ fallback = '/' }) => {
  const navigate = useNavigate();

  useEffect(() => {
    const historyIdx = window.history.state?.idx ?? 0;
    if (historyIdx > 0) {
      navigate(-1);
      return;
    }
    navigate(fallback, { replace: true });
  }, [navigate, fallback]);

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '40vh',
    }}
    >
      <Spin size="large" />
    </div>
  );
};

const ProtectedRoute = ({ children, permission, permissions, requireAll = false, superAdminOnly = false }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const { can, canAny, canAll, isSuperAdmin } = usePermissions();
  const { subscription, loadingSubscription } = useAppData();
  const location = useLocation();
  
  // Double-check super admin status directly from user object
  const isSuperAdminUser = user?.is_super_admin === true;

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh' 
      }}>
        <Spin size="large" />
      </div>
    );
  }

  // Only redirect to login if not authenticated AND not loading
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Platform admin panel: only users with is_super_admin === true (not roles/permissions)
  const pathname = location.pathname;
  const adminRoute = isSuperAdminRoute(pathname) || superAdminOnly;
  const isAllowedSuperAdminUserPath = pathname === '/'
    || pathname === '/me'
    || pathname.startsWith('/me/')
    || pathname === '/settings'
    || pathname.startsWith('/settings/')
    || pathname === '/change-password';

  if (isSuperAdmin || isSuperAdminUser) {
    // If it's not an admin route and not an allowed path, block it
    if (!adminRoute && !isAllowedSuperAdminUserPath) {
      return <Navigate to="/users" replace />;
    }
  } else if (adminRoute) {
    return <RedirectBack fallback="/" />;
  }

  // Check subscription status early to prevent unnecessary API calls
  // Allow access to upgrade page, profile/settings, and dashboard (for wizard)
  const allowedPaths = ['/upgrade', '/me', '/settings', '/change-password', '/'];
  const isAllowedPath = allowedPaths.some(path => location.pathname === path || location.pathname.startsWith(path));
  
  // Wait for subscription to load, but don't block if still loading
  // Don't redirect dashboard - let it handle wizard/upgrade flow
  if (!loadingSubscription && !isSuperAdmin && !isSuperAdminUser && !isAllowedPath) {
    // Check if account is locked
    if (subscription && subscription.is_locked) {
      return <Navigate to="/upgrade" replace />;
    }

    // Check if subscription is not active
    if (subscription && !subscription.is_fully_active) {
      return <Navigate to="/upgrade" replace />;
    }

    // Only redirect if subscription is confirmed missing (not just loading)
    // Dashboard will handle showing wizard or upgrade page as needed
    if (!subscription && location.pathname !== '/') {
      return <Navigate to="/upgrade" replace />;
    }
  }

  // Check permissions if provided
  if (permission) {
    if (!can(permission) && !isSuperAdmin && !isSuperAdminUser) {
      return (
        <Result
          status="403"
          title="403"
          subTitle="Sorry, you don't have permission to access this page."
          icon={<LockOutlined />}
          extra={
            <Button type="primary" onClick={() => window.history.back()}>
              Go Back
            </Button>
          }
        />
      );
    }
  }

  if (permissions && permissions.length > 0) {
    const hasPermission = requireAll 
      ? canAll(permissions) 
      : canAny(permissions);
    
    if (!hasPermission && !isSuperAdmin && !isSuperAdminUser) {
      return (
        <Result
          status="403"
          title="403"
          subTitle="Sorry, you don't have permission to access this page."
          icon={<LockOutlined />}
          extra={
            <Button type="primary" onClick={() => window.history.back()}>
              Go Back
            </Button>
          }
        />
      );
    }
  }

  return children;
};

export default ProtectedRoute;

